
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Load, LoadSchedule } from './types';
import ScheduleItem from './schedule-components/ScheduleItem';
import EmptyScheduleMessage from './schedule-components/EmptyScheduleMessage';

interface LoadSchedulingModalProps {
  load: Load;
  onClose: () => void;
  onSave: (loadId: string, schedules: LoadSchedule[]) => void;
}

const LoadSchedulingModal: React.FC<LoadSchedulingModalProps> = ({ load, onClose, onSave }) => {
  const [schedules, setSchedules] = useState<LoadSchedule[]>([...load.schedules]);

  const handleAddSchedule = () => {
    const newSchedule: LoadSchedule = {
      id: `${load.id}-${uuidv4().split('-')[0]}`, // Create a shorter unique ID
      days: [],
      startTime: '08:00',
      endTime: '17:00',
      enabled: true,
    };
    
    setSchedules([...schedules, newSchedule]);
  };

  const handleRemoveSchedule = (scheduleId: string) => {
    setSchedules(schedules.filter(schedule => schedule.id !== scheduleId));
  };

  const handleDayToggle = (scheduleId: string, day: LoadSchedule['days'][0]) => {
    setSchedules(schedules.map(schedule => {
      if (schedule.id === scheduleId) {
        const days = schedule.days.includes(day)
          ? schedule.days.filter(d => d !== day)
          : [...schedule.days, day];
        return { ...schedule, days };
      }
      return schedule;
    }));
  };

  const handleTimeChange = (
    scheduleId: string, 
    field: 'startTime' | 'endTime', 
    value: string
  ) => {
    setSchedules(schedules.map(schedule => {
      if (schedule.id === scheduleId) {
        return { ...schedule, [field]: value };
      }
      return schedule;
    }));
  };

  const handleToggleEnabled = (scheduleId: string) => {
    setSchedules(schedules.map(schedule => {
      if (schedule.id === scheduleId) {
        return { ...schedule, enabled: !schedule.enabled };
      }
      return schedule;
    }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Schedule: {load.name}</DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-6">
          {schedules.length === 0 ? (
            <EmptyScheduleMessage />
          ) : (
            schedules.map((schedule, index) => (
              <ScheduleItem
                key={schedule.id}
                schedule={schedule}
                index={index}
                onRemove={handleRemoveSchedule}
                onDayToggle={handleDayToggle}
                onTimeChange={handleTimeChange}
                onToggleEnabled={handleToggleEnabled}
              />
            ))
          )}
          
          <Button 
            variant="outline" 
            onClick={handleAddSchedule}
            className="w-full border-dashed border-gray-300 dark:border-gray-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Schedule
          </Button>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSave(load.id, schedules)}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoadSchedulingModal;
